import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Layers, Plus, FolderPlus, LayoutGrid, Shuffle, Trash2 } from 'lucide-react';
import ConditionRow from './ConditionRow';
import { DragHandle, DropGap, IntoGroupDropTarget, PairDropTarget } from './CriteriaDragContext';
import {
  clampSamplePct,
  emptyCondition,
  emptyGroup,
  groupContributionEstimate,
  groupSamplePct,
  pathKey,
} from './criteriaUtils';

const ToggleOp = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-hairline bg-[#F8FAFC] p-0.5 text-xs font-medium" role="radiogroup" aria-label="Combine conditions with">
    {[
      { v: 'AND', label: 'All of' },
      { v: 'OR', label: 'Any of' },
    ].map((o) => (
      <button
        key={o.v}
        type="button"
        role="radio"
        aria-checked={value === o.v}
        onClick={() => onChange(o.v)}
        className={`rounded-md px-2.5 py-1 transition-colors ${
          value === o.v ? 'bg-white text-[#18181B] shadow-sm' : 'text-zinc-500 hover:text-[#18181B]'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * Percentage of this group's matches to keep, with a live estimate of how many
 * profiles that is. Reshuffle picks a different sample of the same size.
 */
const SampleControl = ({ pct, isRoot, estimate, estimateStale, onPctChange, onReshuffle }) => {
  // The number box needs its own draft so a half-typed value is not clamped mid-keystroke.
  const [draft, setDraft] = useState(String(pct));
  useEffect(() => {
    setDraft(String(pct));
  }, [pct]);

  const commitDraft = () => {
    const n = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(n)) {
      setDraft(String(pct));
      return;
    }
    const next = clampSamplePct(n);
    setDraft(String(next));
    if (next !== pct) onPctChange(next);
  };

  const sampled = pct < 100;
  const label = isRoot ? 'of everyone matching' : 'of this group';

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
      <span>Take</span>
      <input
        type="range"
        min="1"
        max="100"
        step="1"
        value={Math.min(100, Math.max(1, Math.round(pct)))}
        onChange={(e) => onPctChange(clampSamplePct(e.target.value))}
        className="h-2 w-32 cursor-pointer accent-[#0062FB]"
        aria-label={`Percentage ${label}`}
      />
      <span className="inline-flex items-center gap-0.5">
        <input
          type="number"
          min="0.01"
          max="100"
          step="0.1"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
            }
          }}
          className="h-7 w-16 rounded border border-input bg-surface px-1.5 text-right text-xs tabular-nums text-[#18181B] focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          aria-label={`Percentage ${label}`}
        />
        <span>%</span>
      </span>
      <span>{label}</span>
      {estimate != null ? (
        <span
          className={estimateStale ? 'text-zinc-400' : 'text-zinc-600'}
          title={estimateStale ? 'Criteria changed — re-count for exact numbers.' : 'Estimated from the last count.'}
        >
          · ≈ {estimate.toLocaleString()} profiles
        </span>
      ) : null}
      {sampled ? (
        <button
          type="button"
          onClick={onReshuffle}
          className="inline-flex items-center gap-1 rounded p-1 text-zinc-400 hover:bg-[#E8EEF5] hover:text-[#18181B]"
          title="Pick a different sample of the same size"
          aria-label="Reshuffle this sample"
        >
          <Shuffle className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
};

/**
 * A criteria group: "All of" / "Any of" header, children (conditions or nested groups),
 * and add/remove controls. The root group is rendered without a card border.
 */
const CriteriaGroupCard = ({
  node,
  path,
  onChange,
  onRemove,
  onAppend,
  onDuplicate,
  onWrap,
  onMoveOut,
  onReorder,
  onSamplePctChange,
  onReshuffleSample,
  newPaths,
  groupCounts,
  countsStale = false,
  ancestorFactor = 1,
  siblingCount = 1,
  organizationId = null,
  onStartFromTemplate,
}) => {
  const isRoot = path.length === 0;
  const children = node.children || [];
  const setOp = (op) => onChange(path, (n) => ({ ...n, op }));
  const joiner = node.op === 'OR' ? 'or' : 'and';
  const idx = isRoot ? 0 : path[path.length - 1];
  // Nesting the same operator twice reads as one flat level, so a new group takes the other.
  const nestedOp = (node.op || 'AND') === 'AND' ? 'OR' : 'AND';

  const pct = groupSamplePct(node);
  const base = groupCounts ? groupCounts[pathKey(path)] : undefined;
  const estimate = base == null ? null : groupContributionEstimate(base, pct, ancestorFactor);
  // Percentages multiply down the tree, so children report what they contribute
  // after this group's own sample has been applied.
  const childFactor = ancestorFactor * (pct / 100);

  if (isRoot && children.length === 0 && onStartFromTemplate) {
    return (
      <div className="px-[26px] pb-12 pt-[52px] text-center">
        <div className="mx-auto mb-[17px] grid h-[42px] w-[42px] place-items-center rounded-[10px] bg-[#eff5ff] text-[#0062fb]">
          <Layers className="h-[22px] w-[22px]" strokeWidth={1.5} />
        </div>
        <h2 className="text-[18px] font-semibold leading-normal text-[#20232c]">Who do you want to reach?</h2>
        <p className="mb-6 mt-2 text-[13px] leading-normal text-[#949aa6]">
          Add your first condition, or use a template to get started.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <button
            type="button"
            onClick={() => onAppend(path, emptyCondition())}
            className="inline-flex min-h-[39px] items-center gap-2 rounded-[7px] border border-[#0062fb] bg-[#0062fb] px-3 py-[7px] text-[13px] leading-normal text-white hover:bg-[#0059e6]"
          >
            <Plus className="h-[17px] w-[17px]" strokeWidth={1.6} />
            Add condition
          </button>
          <button
            type="button"
            onClick={onStartFromTemplate}
            className="inline-flex min-h-[39px] items-center gap-2 rounded-[7px] border border-[#dfe6f0] bg-white px-3 py-[7px] text-[13px] leading-normal text-[#20232c] hover:bg-[#f1f5fc]"
          >
            <LayoutGrid className="h-[17px] w-[17px]" strokeWidth={1.5} />
            Start from template
          </button>
        </div>
        <p className="mb-0 mt-5 text-[11px] leading-normal text-[#9ba3b0]">
          You can add more conditions and groups as you go.
        </p>
      </div>
    );
  }

  const header = (
    <div className="flex flex-wrap items-center gap-2">
      {!isRoot ? <DragHandle path={path} label="Drag group to move" /> : null}
      <span className="text-sm text-zinc-600">{isRoot ? 'Include profiles matching' : 'Matching'}</span>
      {node.display_name ? (
        <span className="text-sm font-medium text-[#18181B]" title={node.offer_feature ? `${node.offer_feature} = ${node.offer_value_label || node.offer_value || ''}` : undefined}>
          {node.display_name}
        </span>
      ) : null}
      <ToggleOp value={node.op || 'AND'} onChange={setOp} />
      <span className="text-sm text-zinc-600">the following:</span>
      {!isRoot ? (
        <span className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onReorder(path, -1)}
            disabled={idx === 0}
            className="rounded p-1.5 text-zinc-400 hover:bg-[#E8EEF5] hover:text-[#18181B] disabled:cursor-not-allowed disabled:opacity-30"
            title="Move up"
            aria-label="Move group up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onReorder(path, 1)}
            disabled={idx >= siblingCount - 1}
            className="rounded p-1.5 text-zinc-400 hover:bg-[#E8EEF5] hover:text-[#18181B] disabled:cursor-not-allowed disabled:opacity-30"
            title="Move down"
            aria-label="Move group down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(path)}
            className="rounded p-1.5 text-xs text-zinc-400 hover:bg-pink-50 hover:text-[#F53D7A]"
            title="Remove group"
            aria-label="Remove group"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ) : null}
    </div>
  );

  const body = (
    <>
      {isRoot ? header : <IntoGroupDropTarget groupPath={path} index={children.length}>{header}</IntoGroupDropTarget>}

      {children.length ? (
        <SampleControl
          pct={pct}
          isRoot={isRoot}
          estimate={estimate}
          estimateStale={countsStale}
          onPctChange={(next) => onSamplePctChange(path, next)}
          onReshuffle={() => onReshuffleSample(path)}
        />
      ) : null}

      <div className="mt-3 space-y-2">
        {children.length === 0 ? (
          <p className="rounded-lg border border-dashed border-hairline px-4 py-6 text-center text-sm text-zinc-400">
            No conditions yet. Add one below.
          </p>
        ) : null}
        {children.map((child, i) => {
          const childPath = [...path, i];
          const key = childPath.join('-');
          const el =
            child.kind === 'group' ? (
              <CriteriaGroupCard
                key={key}
                node={child}
                path={childPath}
                onChange={onChange}
                onRemove={onRemove}
                onAppend={onAppend}
                onDuplicate={onDuplicate}
                onWrap={onWrap}
                onMoveOut={onMoveOut}
                onReorder={onReorder}
                onSamplePctChange={onSamplePctChange}
                onReshuffleSample={onReshuffleSample}
                newPaths={newPaths}
                groupCounts={groupCounts}
                countsStale={countsStale}
                ancestorFactor={childFactor}
                siblingCount={children.length}
                organizationId={organizationId}
              />
            ) : (
              <ConditionRow
                key={key}
                condition={child}
                path={childPath}
                onChange={onChange}
                onRemove={onRemove}
                onDuplicate={onDuplicate}
                onWrap={onWrap}
                onMoveOut={onMoveOut}
                onReorder={onReorder}
                siblingCount={children.length}
                autoOpenPicker={newPaths?.has(key)}
                organizationId={organizationId}
              />
            );
          return (
            <React.Fragment key={key}>
              {i > 0 ? (
                <div className="flex items-center gap-2 pl-2" aria-hidden>
                  <span className="rounded bg-[#E8EEF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{joiner}</span>
                  <span className="h-px flex-1 bg-hairline" />
                </div>
              ) : null}
              <DropGap parentPath={path} index={i} />
              {child.kind === 'group' ? el : <PairDropTarget path={childPath}>{el}</PairDropTarget>}
            </React.Fragment>
          );
        })}
        {children.length ? <DropGap parentPath={path} index={children.length} /> : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onAppend(path, emptyCondition())}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#0062FB]/30 bg-blue-50/40 px-3 py-1.5 text-sm font-medium text-[#0062FB] hover:bg-blue-50"
        >
          <Plus className="h-4 w-4" />
          Add condition
        </button>
        <button
          type="button"
          onClick={() => onAppend(path, { ...emptyGroup(), op: nestedOp })}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-zinc-600 hover:bg-[#E8EEF5] hover:text-[#18181B]"
          title={`Add a nested group combining its conditions with "${nestedOp === 'AND' ? 'All of' : 'Any of'}"`}
        >
          <FolderPlus className="h-4 w-4" />
          Add group
        </button>
      </div>
    </>
  );

  if (isRoot) return <div>{body}</div>;
  return <div className="rounded-xl border border-hairline bg-[#F8FAFC] p-3 pl-4 shadow-[inset_3px_0_0_#e2e8f0]">{body}</div>;
};

export default CriteriaGroupCard;
